import {
  DashboardResolveStreamLine,
  type DashboardParameterValue,
  type DashboardParameter,
  type DashboardResolveRequest,
  type DashboardResolveWidgetLine,
  type Provenance,
} from "@t3tools/contracts";
import { Exit, Schema } from "effect";

import { artifactRequestParameters, type ArtifactParameterValues } from "./artifactParameters";

/**
 * The client side of `POST /api/v1/dashboards/artifacts/:ref/resolve`: read
 * the NDJSON body line by line, fold each line into per-widget state, and
 * derive what each widget shows while the stream is still open.
 */

export type ArtifactResolveEvent =
  | { readonly type: "line"; readonly line: DashboardResolveWidgetLine }
  /** A line for this widget arrived but did not match the contract. */
  | { readonly type: "malformed"; readonly widgetId: string; readonly message: string }
  | { readonly type: "done"; readonly errors: number };

/** What the stream has delivered so far, keyed by widget id. */
export type ArtifactResolveData = {
  readonly lines: Readonly<Record<string, DashboardResolveWidgetLine>>;
  readonly malformed: Readonly<Record<string, string>>;
  readonly done: boolean;
};

export const EMPTY_ARTIFACT_RESOLVE: ArtifactResolveData = {
  lines: {},
  malformed: {},
  done: false,
};

export class ArtifactResolveStreamError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ArtifactResolveStreamError";
  }
}

const decodeStreamLine = Schema.decodeUnknownExit(DashboardResolveStreamLine);

/** One NDJSON line as an event, or `null` for a line that names no widget. */
export function parseArtifactResolveLine(text: string): ArtifactResolveEvent | null {
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch {
    return null;
  }
  const decoded = decodeStreamLine(json);
  if (Exit.isSuccess(decoded)) {
    const line = decoded.value;
    return "done" in line
      ? { type: "done", errors: line.errors }
      : { type: "line", line: line as DashboardResolveWidgetLine };
  }
  const widgetId =
    typeof json === "object" && json !== null ? (json as { widgetId?: unknown }).widgetId : null;
  return typeof widgetId === "string"
    ? { type: "malformed", widgetId, message: "The server sent malformed data for this widget" }
    : null;
}

/**
 * Reads an NDJSON resolve response. Lines are yielded as they arrive; a body
 * that ends without its done line throws, so widgets still waiting can say so.
 */
export async function* readArtifactResolveStream(
  response: Response,
  signal?: AbortSignal,
): AsyncGenerator<ArtifactResolveEvent> {
  if (!response.body) throw new ArtifactResolveStreamError("The dashboard stream was empty");
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let done = false;
  try {
    while (!done) {
      if (signal?.aborted) return;
      const chunk = await reader.read();
      buffer += decoder.decode(chunk.value, { stream: !chunk.done });
      const lines = buffer.split("\n");
      buffer = chunk.done ? "" : lines.pop()!;
      for (const text of lines) {
        if (!text.trim()) continue;
        const event = parseArtifactResolveLine(text);
        if (!event) continue;
        yield event;
        if (event.type === "done") done = true;
      }
      if (chunk.done) break;
    }
  } finally {
    // Stops a fixture or network body that would otherwise keep producing.
    void reader.cancel().catch(() => undefined);
  }
  if (!done) {
    throw new ArtifactResolveStreamError("The dashboard stopped loading before this widget");
  }
}

export function reduceArtifactResolve(
  data: ArtifactResolveData,
  event: ArtifactResolveEvent,
): ArtifactResolveData {
  switch (event.type) {
    case "line":
      return { ...data, lines: { ...data.lines, [event.line.widgetId]: event.line } };
    case "malformed":
      return { ...data, malformed: { ...data.malformed, [event.widgetId]: event.message } };
    case "done":
      return { ...data, done: true };
  }
}

/**
 * The whole dashboard's request: every parameter, with date range presets
 * resolved to dates so the query key changes when "last 30 days" rolls over.
 */
export function artifactResolveRequest(
  parameters: ReadonlyArray<DashboardParameter>,
  versionId: string,
  values: ArtifactParameterValues,
  now: Date,
): DashboardResolveRequest & { versionId: string } {
  return {
    versionId,
    parameters: artifactRequestParameters(parameters, values, now) as Record<
      string,
      DashboardParameterValue
    >,
  };
}

/** A widget's data as it stands mid-stream. */
export type ArtifactWidgetState =
  | { readonly status: "loading" }
  | {
      readonly status: "ready";
      readonly line: DashboardResolveWidgetLine;
      /** The line is from the previous filters while the new stream catches up. */
      readonly refreshing: boolean;
    }
  | {
      readonly status: "error";
      readonly message: string;
      readonly provenance: ReadonlyArray<Provenance>;
      readonly refreshing: boolean;
    };

export const LOADING_WIDGET: ArtifactWidgetState = { status: "loading" };

function stateFromLine(line: DashboardResolveWidgetLine, refreshing: boolean): ArtifactWidgetState {
  return line.resolved.kind === "error"
    ? { status: "error", message: line.resolved.message, provenance: line.provenance, refreshing }
    : { status: "ready", line, refreshing };
}

/**
 * The current stream's line wins; until it arrives a widget keeps the
 * previous filters' line (dimmed), or a skeleton on first load. A stream that
 * failed or finished without the widget's line is that widget's error.
 */
export function artifactWidgetState(input: {
  current: ArtifactResolveData | undefined;
  error: Error | null;
  previous: ArtifactResolveData | undefined;
  widgetId: string;
}): ArtifactWidgetState {
  const { current, previous, widgetId } = input;
  const line = current?.lines[widgetId];
  if (line) return stateFromLine(line, false);
  const malformed = current?.malformed[widgetId];
  if (malformed) return { status: "error", message: malformed, provenance: [], refreshing: false };
  if (input.error) {
    return { status: "error", message: input.error.message, provenance: [], refreshing: false };
  }
  if (current?.done) {
    return {
      status: "error",
      message: "The server returned no data for this widget",
      provenance: [],
      refreshing: false,
    };
  }
  const fallback = previous?.lines[widgetId];
  return fallback ? stateFromLine(fallback, true) : LOADING_WIDGET;
}
